import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material';
import { config } from '../config/environment';

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

interface AdBannerProps {
  /** Ad slot ID from AdSense. If omitted, uses auto ad format. */
  slot?: string;
  /** Ad format — defaults to 'auto' for responsive */
  format?: string;
  /** Style overrides for the container */
  style?: React.CSSProperties;
}

/**
 * Google AdSense banner component.
 * Renders a responsive horizontal banner ad.
 * Falls back gracefully if ads are blocked or not yet approved.
 */
const AdBanner: React.FC<AdBannerProps> = ({
  slot,
  format = 'auto',
  style,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const adRef = useRef<HTMLDivElement>(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (!config.ads.enabled) return; // ads hidden until approval — don't request fills
    if (pushed.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (e) {
      // AdSense not loaded (ad blocker, not approved yet, etc.)
    }
  }, []);

  // Render nothing until ads are enabled — no empty placeholder box, no push().
  if (!config.ads.enabled) return null;

  return (
    <div
      ref={adRef}
      style={{
        width: '100%',
        maxWidth: '728px',
        margin: '32px auto',
        minHeight: '90px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: '12px',
        overflow: 'hidden',
        ...style,
      }}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }}
        data-ad-client="ca-pub-9590574276416808"
        data-ad-slot={slot || ''}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdBanner;
